import { print } from "graphql";
import gql from "graphql-tag";
import { useInfiniteQuery } from "react-query";
import { getFreshAccessToken } from "../use-user";

const GET_TAKEBACKS = gql`
  query GetTakebacks(
    $limit: Int!
    $offset: Int!
    $orderBy: String
    $orderDirection: String
    $filter: TakebackFilterInput
  ) {
    takebacks(
      pagination: {
        limit: $limit
        offset: $offset
        orderBy: $orderBy
        orderDirection: $orderDirection
      }
      filter: $filter
    ) {
      items {
        id
        data
        takeback_processing_historys {
          items {
            processed_at
            status
            updated_at
          }
        }
        shippingss {
          items {
            tracking_number
          }
        }
        customer_id_belongsTo {
          name
          last_name
          id
        }
      }
      pageInfo {
        totalCount
        hasPreviousPage
        hasNextPage
      }
    }
  }
`;

export const useGetTakebacksInfinite = ({
  limit = 100,
  orderBy = "updated_at",
  orderDirection = "DESC",
  filter = { product_id: { eq: "lego-bricks" } }, // Default filter to maintain backward compatibility
  refreshKey, // Add refreshKey parameter
  ...options
} = {}) => {
  return useInfiniteQuery({
    queryKey: [
      "takebacks-infinite",
      limit,
      orderBy,
      orderDirection,
      filter,
      refreshKey,
    ],
    queryFn: async ({ pageParam = 0 }) => {
      const accessToken = getFreshAccessToken();
      const res = await fetch(`${process.env.REACT_APP_TES_URL}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-client-id": process.env.REACT_APP_CLIENT_ID || "lego-test",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          query: print(GET_TAKEBACKS),
          variables: {
            limit,
            offset: pageParam,
            orderBy,
            orderDirection,
            filter,
          },
        }),
      });
      const data = await res.json();

      if (data.errors) {
        const error = new Error(data.errors[0]?.message || "GraphQL error");
        // Mark this as a GraphQL error so we can handle it differently
        error.isGraphQLError = true;
        throw error;
      }

      return data;
    },
    getNextPageParam: (lastPage, allPages) => {
      // If the last page had errors or no data, stop pagination
      if (!lastPage?.data || lastPage?.errors) return undefined;

      const pageInfo = lastPage?.data?.takebacks?.pageInfo;
      if (!pageInfo?.hasNextPage) return undefined;

      // Calculate the next offset based on the number of items fetched so far
      const currentOffset = (allPages.length - 1) * limit;
      const nextOffset = currentOffset + limit;

      return nextOffset;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: (failureCount, error) => {
      // Don't retry on GraphQL errors - these are usually data issues that won't resolve
      if (error?.isGraphQLError) return false;
      // Don't retry on 4xx errors (client errors)
      if (error?.message?.includes("4")) return false;
      // Retry up to 2 times for other errors (network issues, 5xx, etc)
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    ...options,
  });
};
